<template>
  <b-modal
    id="add-connection"
    size="lg"
    title="Add Connection"
    ref="modal"
    ok-title="Add"
    @ok="handleOk"
    @show="onReset"
    :cancel-disabled="disableConfirmButtons"
    :ok-disabled="disableConfirmButtons"
    :no-close-on-backdrop="true"
    hide-header-close
  >
    <loading
      :active.sync="isLoading"
      loader="spinner"
      color="#20A8D8"
      :is-full-page="false"
    />

    <b-form @submit.stop.prevent="handleSubmit" novalidate>
      <b-container fluid>
        <!-- Select Connection -->
        <b-row class="my-2">
          <b-col sm="8">
            <b>SELECT CONNECTION</b>
          </b-col>
        </b-row>
        <b-row>
          <b-col lg="6" md="6" sm="12" class="my-1">
            <b-form-group label="Type">
              <v-select
                name="Type"
                class="style-chooser"
                label="text"
                placeholder=" - Please select - "
                :options="connectionSelTypes"
                :reduce="(item) => item.value"
                v-model="selType"
              >
              </v-select> </b-form-group
          ></b-col>
        </b-row>
        <b-row>
          <b-col lg="6" md="6" sm="12" class="my-1">
            <b-form-group label="Source">
              <v-select
                name="Source"
                class="style-chooser"
                label="text"
                placeholder=" - Please select - "
                :options="fromCompanyOptions"
                :reduce="(company) => company.value"
                :multiple="isSourceMultiSelection"
                @input="onSelectSource"
                v-model="selFromCompany"
                v-validate="'selectRequired'"
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching">
                    No results found for
                    <em>
                      <strong>{{ search }}</strong>
                    </em>
                  </template>
                  <em :style="{ opacity: 0.5 }" v-else>
                    Start typing to search for a company
                  </em>
                </template>
              </v-select>
              <span v-show="errors.has('Source')" class="help-block">{{
                errors.first("Source")
              }}</span>
            </b-form-group>
          </b-col>
          <b-col lg="6" md="6" sm="12" class="my-1">
            <b-form-group label="Destination">
              <v-select
                name="Destination"
                class="style-chooser"
                label="text"
                placeholder=" - Please select - "
                :options="toCompanyOptions"
                :reduce="(company) => company.value"
                :multiple="isDestinationMultiSelection"
                @input="onSelectDestination"
                v-model="selToCompany"
                v-validate="'selectRequired'"
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching">
                    No results found for
                    <em>
                      <strong>{{ search }}</strong>
                    </em>
                  </template>
                  <em :style="{ opacity: 0.5 }" v-else>
                    Start typing to search for a company
                  </em>
                </template>
              </v-select>
              <span v-show="errors.has('Destination')" class="help-block">{{
                errors.first("Destination")
              }}</span>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Select Locations -->
        <b-row class="mt-4">
          <b-col sm="6" md="6">
            <span class="details-view-title">SELECT LOCATIONS</span>
            <div class="details-view-subtitle">
              List of destination company storage locations
            </div>
          </b-col>
          <b-col sm="6" md="4" offset-md="2" class="mb-2 text-md-right">
            <b-input-group prepend="Show" append="/ Page">
              <b-form-select :options="pageOptions" v-model="perPage" />
            </b-input-group>
          </b-col>
        </b-row>
        <b-table
          show-empty
          striped
          hover
          :items="storageLocationItems"
          :fields="fields"
          :current-page="currentPage"
          :per-page="perPage"
          :filter="filter"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          responsive
        >
          <template v-slot:cell(selection)="row">
            <b-form-checkbox
              :id="row.item.id"
              :checked="isIncluded(row.item.id)"
              @change="onCheckboxChange(row.item.id, $event)"
              value="true"
              unchecked-value="false"
            />
          </template>
          <template v-slot:cell(geoaddress)="row">
            <span class="numFont">
              ({{ row.item.geoaddress.latitude }}, {{ row.item.geoaddress.longitude }})
            </span>
          </template>
        </b-table>
        <b-row>
          <b-col md="8" sm="12" class="my-1">
            <span class="total-display"
              >Total: {{ totalRows ? totalRows.toLocaleString() : 0 }}</span
            >
          </b-col>
          <b-col md="4" sm="12" class="my-1">
            <b-pagination
              align="right"
              :total-rows="totalRows"
              :per-page="perPage"
              v-model="currentPage"
              class="my-0"
            />
          </b-col>
        </b-row>

        <!-- Other Details -->
        <b-row class="mt-4 mb-2">
          <b-col sm="8">
            <b>OTHER DETAILS</b>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col lg="6" md="6" sm="12">
            <b-form-group label="Connection Types">
              <b-form-text class="connection-types"
                >Select at least 1 connection type</b-form-text
              >
              <b-form-checkbox-group
                stacked
                id="connectionTypes"
                name="Connection Types"
                v-model="form.connections"
              >
                <b-form-checkbox
                  v-bind:key="conType.id"
                  :value="conType.value"
                  v-for="conType in connectionTypes"
                  >{{ conType.text }}
                </b-form-checkbox>
              </b-form-checkbox-group>
              <b-form-group
                v-show="showOthersField"
                label="If Others, please specify"
                label-for="others"
                class="mt-2"
              >
                <b-form-input
                  name="Others"
                  v-model="form.others"
                  type="text"
                  v-validate="{
                    required: showOthersField,
                  }"
                  placeholder="Please specify"
                ></b-form-input>
                <span v-show="errors.has('Others')" class="help-block">{{
                  errors.first("Others")
                }}</span>
              </b-form-group>
            </b-form-group>
          </b-col>
          <b-col lg="6" md="6" sm="12">
            <b-form-group label="Notes">
              <b-form-textarea
                id="notes"
                name="Notes"
                v-model="form.notes"
                placeholder="Other info here"
                :rows="2"
                :max-rows="6"
                maxlength="200"
                v-validate="{
                  required: true,
                  regex: remarksRegex,
                }"
              />
              <span v-show="errors.has('Notes')" class="help-block">{{
                errors.first("Notes")
              }}</span>
            </b-form-group>
          </b-col>
        </b-row>
      </b-container>
    </b-form>
  </b-modal>
</template>

<script>
// Util
import { ConnectionUtil } from "@/utils/connectionUtil";
import { DropDownItemsUtil } from "@/utils/dropDownItemsUtil";
import { LocationUtil } from "@/utils/locationUtil";
import { ValidationUtil } from "@/utils/validationUtil";

// DAO
import companyDAO from "@/database/companies";
import connectionsDAO from "@/database/connections";

// Others
import EventBus from "@/shared/event-bus";
import config from "@/config/env-constants";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import _ from "lodash";

export default {
  name: "add-connection",
  components: {
    Loading,
  },
  props: {
    allCompaniesObj: {
      type: Object,
      required: true,
    },
    allConnectedCompaniesObj: {
      type: Object,
      required: true,
    },
    allStorageLocationsObj: {
      type: Object,
      required: true,
    },
    connectionTypes: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      fields: [
        {
          key: "selection",
          label: "#",
          sortable: false,
        },
        {
          key: "name",
          sortable: true,
        },
        {
          key: "address",
          sortable: false,
        },
        "geoaddress",
      ],
      currentPage: 1,
      perPage: 5,
      totalRows: 0,
      pageOptions: [5, 10, 15, 25, 50, 100],
      sortBy: null,
      sortDesc: false,
      sortDirection: "asc",
      filter: null,

      form: { ...ConnectionUtil.getDefaultConnectionObj() },
      selFromCompany: null,
      selToCompany: null,
      fromCompanyOptions: {},
      toCompanyOptions: {},
      storageLocationItems: [],
      storageLocationsObj: {},
      showOthersField: false,

      selType: "",
      connectionSelTypes: [],
      isSourceMultiSelection: false,
      isDestinationMultiSelection: true,

      isSuperAdmin: this.$store.getters.isSuperAdmin,
      loggedUser: this.$store.getters.loggedUser,
      loggedUserCompany: this.$store.getters.loggedUserCompany,
      // Check for loader
      isLoading: false,
    };
  },
  mounted() {
    this.onReset();

    if (this.isSuperAdmin) {
      this.connectionSelTypes = [
        ...config.connectionSelTypes.company,
        ...config.connectionSelTypes.superAdmin,
      ];
    } else {
      this.connectionSelTypes = [...config.connectionSelTypes.company];
    }
  },
  watch: {
    "form.connections": function () {
      if (this.form.connections && this.form.connections.includes("Others")) {
        this.showOthersField = true;
      } else {
        this.showOthersField = false;
      }
    },
    selToCompany: function (newVal) {
      if (newVal) {
        this.retrieveStorageLocations(newVal);
      }
    },
    selType: function (newVal) {
      // Reset Source and Destination
      this.selFromCompany = null;
      this.selToCompany = null;
      this.storageLocationItems = [];
      this.storageLocationsObj = {};

      switch (newVal) {
        case "P2C":
          this.isSourceMultiSelection = false;
          this.isDestinationMultiSelection = true;
          this.fromCompanyOptions = DropDownItemsUtil.retrieveParentCompanies(
            this.allCompaniesObj
          );
          this.toCompanyOptions = DropDownItemsUtil.retrieveChildCompanies(
            this.allCompaniesObj,
            false
          );
          break;
        case "C2P":
          this.isSourceMultiSelection = true;
          this.isDestinationMultiSelection = false;

          this.fromCompanyOptions = DropDownItemsUtil.retrieveChildCompanies(
            this.allCompaniesObj
          );
          this.toCompanyOptions = DropDownItemsUtil.retrieveParentCompanies(
            this.allCompaniesObj,
            false
          );
          break;
        case "C2C":
          this.isSourceMultiSelection = this.isSuperAdmin ? true : false;
          this.isDestinationMultiSelection = this.isSuperAdmin ? true : false;
          this.fromCompanyOptions = DropDownItemsUtil.retrieveChildCompanies(
            this.allCompaniesObj
          );
          this.toCompanyOptions = DropDownItemsUtil.retrieveChildCompanies(
            this.allCompaniesObj,
            false
          );
          break;
        case "P2P":
          this.isSourceMultiSelection = true;
          this.isDestinationMultiSelection = true;
          this.fromCompanyOptions = DropDownItemsUtil.retrieveParentCompanies(
            this.allCompaniesObj
          );
          this.toCompanyOptions = DropDownItemsUtil.retrieveParentCompanies(
            this.allCompaniesObj,
            false
          );
          break;
      }
    },
  },
  computed: {
    disableConfirmButtons() {
      return this.isLoading;
    },
    remarksRegex() {
      return config.remarksRegex;
    },
    sources() {
      return Array.isArray(this.selFromCompany)
        ? this.selFromCompany.slice()
        : this.selFromCompany
        ? [this.selFromCompany]
        : [];
    },
    destinations() {
      return Array.isArray(this.selToCompany)
        ? this.selToCompany.slice()
        : this.selToCompany
        ? [this.selToCompany]
        : [];
    },
  },
  methods: {
    async handleOk(evt) {
      // Prevent modal from closing
      evt.preventDefault();

      // show loading indicator
      this.isLoading = true;

      let isValid = await this.$validator.validateAll();
      if (!isValid) {
        this.$toaster.warning("Please address the field/s with invalid input.");
        this.isLoading = false;
        return;
      }

      let existsConnection = await this.hasExistingConnection();
      if (existsConnection) {
        this.$toaster.warning(
          `There is already an existing connection between ${existsConnection}.`
        );
        this.isLoading = false;
        return;
      } else if (this.sources.length == 0) {
        this.$toaster.warning("Select source");
        this.isLoading = false;
        return;
      } else if (this.destinations.length == 0) {
        this.$toaster.warning("Select destination");
        this.isLoading = false;
      }
      // At least 1 connection required
      else if (this.form.connections.length <= 0) {
        this.$toaster.warning(
          "You should have at least 1 connection type between the selected companies."
        );
        this.isLoading = false;
        return;
      }
      // if Others is selected, the others field is required
      else if (this.form.connections.includes("Others") && this.form.others.length <= 0) {
        this.$toaster.warning("Please fill-in the Others field to proceed.");
        this.isLoading = false;
        return;
      }
      // At least 1 storage location must be connected
      else if (!this.hasAtLeastOneStorageLocation()) {
        this.$toaster.warning("Please select at least 1 storage location to connect.");
        this.isLoading = false;
        return;
      }

      if (this.getParam().length > 0) {
        await this.handleSubmit();
      } else {
        this.isLoading = false;
      }
    },
    hasAtLeastOneStorageLocation() {
      let hasStorageLocation = false;
      let locArr = Object.values(this.storageLocationsObj);
      for (const loc of locArr) {
        if (loc.isIncluded === "true") {
          hasStorageLocation = true;
          break;
        }
      }
      return hasStorageLocation;
    },
    async hasExistingConnection() {
      let existingConnections = [];
      if (this.sources.length > 0 && this.destinations.length > 0) {
        let connectionPromises = [];

        for (const source of this.sources) {
          for (const destination of this.destinations) {
            connectionPromises.push(
              connectionsDAO.getConnection(source.id, destination.id)
            );
          }
        }

        const connectionResults = await Promise.all(connectionPromises);

        connectionResults.forEach((conn, index) => {
          if (!_.isEmpty(conn)) {
            existingConnections.push(
              `${conn.company.name} - ${conn.connectedCompany.name}`
            );
          }
        });
      }
      return existingConnections.join(", ");
    },

    getParam() {
      // Removes excess whitespace
      let params = [];

      this.form.notes = ValidationUtil.removeExcessWhiteSpace(this.form.notes);
      this.form.others = ValidationUtil.removeExcessWhiteSpace(this.form.others);

      for (const source of this.sources) {
        this.updateFromCompany(source);
        for (const destination of this.destinations) {
          this.updateToCompany(destination);
          
          const matchingLocations = Object.entries(this.storageLocationsObj || {}).filter(
            ([key, value]) => value.companyId === destination.id && value.isIncluded === "true"
          );

          if (matchingLocations.length !== 0) {
            this.form.storageLocations = Object.fromEntries(matchingLocations);
            params.push(_.cloneDeep(this.form));
          } else {
            // If no storage locations are selected for the destination
            this.$toaster.warning(`Please select at least 1 storage location for ${destination.name}.`);
            params = [];
            break;
          }
        }
      }

      return params;
    },
    updateFromCompany(source) {
      this.form.company = this.getCompany(source.id);
      this.form.company.geoaddress = LocationUtil.getGeoaddress(
        this.form.company.geoaddress
      );
      this.form.companyId = source.id;
    },
    updateToCompany(destination) {
      this.form.connectedCompany = this.getCompany(destination.id);
      this.form.connectedCompany.geoaddress = LocationUtil.getGeoaddress(
        this.form.connectedCompany.geoaddress
      );
      this.form.connectedCompanyId = destination.id;
    },
    getCompany(id) {
      if (this.allCompaniesObj[id]) {
        return this.allCompaniesObj[id];
      } else if (this.allConnectedCompaniesObj[id]) {
        return this.allConnectedCompaniesObj[id];
      }
      return { ...config.companyDefaultValue };
    },
    isIncluded(id) {
      return (
        this.storageLocationsObj[id] && this.storageLocationsObj[id].isIncluded === "true"
      );
    },
    onCheckboxChange(id, event) {
      if (this.storageLocationsObj[id]) {
        this.storageLocationsObj[id].isIncluded = event;
      }
    },
    onSelectSource() {
      if (this.selFromCompany) {
        const isSourceArray = Array.isArray(this.selFromCompany);

        // Check if selected source is present in the destination
        let matchingIndex = -1;
        for (var i = 0; i < this.destinations.length; i++) {
          const index = this.sources.findIndex((s) => s.id === this.destinations[i].id);
          if (index >= 0) {
            matchingIndex = index;
            break;
          }
        }

        // If has match
        if (matchingIndex > -1) {
          // If source is array remove the matching index in the list
          // else set the variable to null
          if (isSourceArray) {
            this.selFromCompany.splice(matchingIndex, 1);
          } else {
            this.selFromCompany = null;
          }

          this.$toaster.warning(`Source and Destination should not be the same`);
        }
      }
    },
    onSelectDestination() {
      if (this.selFromCompany) {
        const isDestinationArray = Array.isArray(this.selToCompany);

        // Check if selected destination is present in the source
        let matchingIndex = -1;
        for (var i = 0; i < this.sources.length; i++) {
          const index = this.destinations.findIndex((s) => s.id === this.sources[i].id);
          if (index >= 0) {
            matchingIndex = index;
            break;
          }
        }

        // If has match
        if (matchingIndex > -1) {
          // If source is array remove the matching index in the list
          // else set the variable to null
          if (isDestinationArray) {
            this.selToCompany.splice(matchingIndex, 1);
          } else {
            this.selToCompany = null;
          }

          this.$toaster.warning(`Source and Destination should not be the same`);
        }
      }
    },
    async handleSubmit() {
      try {
        // show loading indicator
        this.isLoading = true;
        const result = await this.$store.dispatch("addConnections", this.getParam());

        if (result.length > 0) {
          // Update companies setup status
          let updateCompanyPromises = [];
          for (const source of this.sources) {
            updateCompanyPromises.push(
              companyDAO.updateCompanySetupStatus({
                companyId: source.id,
                data: {
                  hasConnection: true,
                },
              })
            );
          }

          await Promise.all(updateCompanyPromises);

          this.$toaster.success(`New Connections was created successfully.`);
          EventBus.$emit("onCloseSaveConnection", result);
          this.$refs.modal.hide();
        } else {
          this.$toaster.error(`Error creating connections. Please try again.`);
        }
      } catch (_error) {
        this.$toaster.error(`Error creating connections. Please try again.`);
      } finally {
        // hide loading indicator
        this.isLoading = false;
      }
    },

    onReset() {
      /* Reset our form values */
      this.form = { ...ConnectionUtil.getDefaultConnectionObj() };

      this.selFromCompany = null;
      this.selToCompany = null;

      this.storageLocationItems = [];
      this.storageLocationsObj = {};

      // reset validation
      this.isLoading = false;
      this.$validator.reset();
      this.errors.clear();

      // Reset source
      this.selType = "P2C";
      this.isSourceMultiSelection = false;
      this.isDestinationMultiSelection = true;
      this.fromCompanyOptions = DropDownItemsUtil.retrieveParentCompanies(
        this.allCompaniesObj
      );
      this.toCompanyOptions = DropDownItemsUtil.retrieveChildCompanies(
        this.allCompaniesObj,
        false
      );
    },

    retrieveStorageLocations(selCompany) {
      let items = Array.isArray(selCompany) ? [...selCompany] : [selCompany];

      // reset values
      this.storageLocationItems = [];
      this.storageLocationsObj = {};

      for (const item of items) {
        let storageLocations = _.filter(this.allStorageLocationsObj, (o) => {
          return o.companyId === item.id;
        });
        _.forEach(storageLocations, (value) => {
          let loc = value;
          this.storageLocationsObj[loc.id] = {
            companyId: loc.companyId,
            id: loc.id,
            name: loc.name,
            address: loc.address,
            geoaddress: LocationUtil.getGeoaddress(loc.geoaddress),
            areaRadius: loc.areaRadius ? loc.areaRadius : 100,
            isIncluded: "false",
            isActive: loc.isActive,
          };

          this.storageLocationItems.push(this.storageLocationsObj[loc.id]);
        });
      }

      this.totalRows = _.size(this.storageLocationItems);
    },
    getCompanyValue(companyObj, fieldName) {
      let companyId = companyObj ? companyObj.id : null;
      if (companyId && this.allCompaniesObj[companyId]) {
        return this.allCompaniesObj[companyId][fieldName];
      }
      return "";
    },
  },
};
</script>

<style scoped>
.connection-types {
  margin-top: -5px;
  margin-bottom: 10px;
}
</style>
